import * as XLSX from 'xlsx';
import type { AiEvaluation, ExtractedField } from '@/types/evaluation';
import { downloadBlob } from '@/utils/download';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import { safeStringify } from '@/utils/jsonHelpers';

/**
 * Field-level export of a single contract review (CSV / XLSX).
 *
 * The column set is a closed vocabulary, exported so the picker in the export
 * modal can be seeded from it rather than hard-coding the same eight strings a
 * second time — the previous copy started empty, and exporting without touching
 * the picker produced a file of bare newlines.
 */
export const DATA_EXPORT_COLUMNS = [
  'Extraction Prompt: Name',
  'Extraction Prompt: Prompt',
  'Extraction Prompt: Example Data',
  'AI: Response',
  'AI: Reason',
  'AI: Page',
  'Evaluation: Value',
  'Evaluation: Reason',
] as const;

export type DataExportColumn = (typeof DATA_EXPORT_COLUMNS)[number];

/**
 * Project the fields onto the chosen columns, **in the order they were chosen**
 * — the picker doubles as the column order, so this must not sort.
 */
export function buildDataExportRows(
  fields: ExtractedField[],
  columns: readonly string[]
): Record<string, unknown>[] {
  return fields.map(column => {
    // `ai_evaluation` is untyped JSON on the wire; the view-model narrows it.
    const aiEval = column.ai_evaluation as AiEvaluation | null | undefined;

    const allData: Record<DataExportColumn, unknown> = {
      'Extraction Prompt: Name': column.field || '',
      'Extraction Prompt: Prompt': column.extraction_prompt ?? '',
      'Extraction Prompt: Example Data': (column.example_data ?? []).join('\n'),
      'AI: Response': formatDisplayValue(column.ai_value, 'str'),
      'AI: Reason': column.ai_reason ?? '',
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      'AI: Page': column.ai_page || '',
      'Evaluation: Value': aiEval?.answer ?? '',
      'Evaluation: Reason': aiEval?.reason ?? '',
    };

    const row: Record<string, unknown> = {};
    for (const key of columns) {
      if (key in allData) {
        row[key] = allData[key as DataExportColumn];
      }
    }
    return row;
  });
}

/** Every value is quoted and its own quotes doubled — RFC 4180. */
function toCsv(rows: Record<string, unknown>[]): string {
  const headers = Object.keys(rows[0]);
  return [
    headers.join(','),
    ...rows.map(row =>
      headers.map(header => `"${safeStringify(row[header] ?? '').replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');
}

export function exportFieldsCsv(rows: Record<string, unknown>[], fileName: string): void {
  downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), `${fileName}.csv`);
}

export function exportFieldsXlsx(rows: Record<string, unknown>[], fileName: string): void {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Evaluation Data');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}
